<template>
  <!-- Statistics Indicators -->
  <div class="statistics-container">
    <div class="stat-card stat-card-blue">
      <div class="stat-icon">
        <i class="bi bi-people"></i>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ statistics.totalStudents }}</div>
        <div class="stat-label">Total Students</div>
      </div>
    </div>

    <div class="stat-card stat-card-purple">
      <div class="stat-icon">
        <i class="bi bi-building"></i>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ statistics.totalInstitutions }}</div>
        <div class="stat-label">Institutions</div>
      </div>
    </div>

    <div class="stat-card stat-card-green">
      <div class="stat-icon">
        <i class="bi bi-diagram-2"></i>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ statistics.totalGrades }}</div>
        <div class="stat-label">Grades</div>
      </div>
    </div>
  </div>

  <div class="StudentListContainer">
    <h1>
      Student database
      <span v-if="selectedGroupName"> - filter: {{ selectedGroupName }}</span>
    </h1>
    <hr>

    <!-- Actions -->
    <div class="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
  <!-- Bloc gauche -->
  <div class="d-flex align-items-center gap-2">
    <button class="btn btn-danger" @click="deleteAllStudents">🗑️</button>
    <button class="btn btn-secondary" @click="resetGroupFilter">Reset Group Filter</button>
  </div>

  <!-- Bloc droite -->
  <div class="d-flex align-items-center gap-2">
    <select v-model="selectedGroupId" class="form-select w-auto">
      <option disabled value="">Select Group</option>
      <option v-for="group in groupList" :key="group._id" :value="group._id">
        {{ group.groupName }}
      </option>
    </select>
    <button class="btn btn-success" @click="addSelectedToGroup" :disabled="!selectedGroupId">
      Add students to the selected group
    </button>
  </div>
</div>





    <!-- Tableau Tabulator (optimisé - suppression du key qui force les recreations) -->
    <TabulatorRemote ref="tabulatorRef" :apiUrl="`/students`" :columns="studentColumns" :filters="computedFilters"
      :onDelete="deleteStudent" selectionMode="multi" @select-items="handleSelectedStudents"
      @edit-student="openStudentModal" tableType="students" :pageSize="30" :height="'1200px'" />
    <StudentModal v-if="showModal" :student="selectedStudent" :onDelete="deleteStudent" :onUpdate="updateStudent"
      @close="showModal = false" />

  </div>
  <!-- @select-item="handleRowClick" -->

  <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1100">
    <div class="toast align-items-center text-bg-success border-0" role="alert" aria-live="assertive" aria-atomic="true"
      ref="successToast">
      <div class="d-flex">
        <div class="toast-body">{{ toastMessage }}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="hideToast"></button>
      </div>
    </div>
  </div>

</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import api from "@/services/axios";
import TabulatorRemote from '@/components/common/TabulatorRemote.vue';
import { studentColumnsBase } from '@/columns/students.js';
import StudentModal from '@/components/students/studentModal.vue';



const props = defineProps({
  filterGroupId: String,
  refreshGroupOptions: Number, 
});

console.log("🧪 filterGroupId au mount :", props.filterGroupId);

const selectedGroupName = computed(() => {
  if (!props.filterGroupId) return null;
  const group = groupList.value.find(g => g._id === props.filterGroupId);
  console.log("🔍 Recherche groupe pour ID:", props.filterGroupId, "trouvé:", group?.groupName);
  return group?.groupName || null;
});

const emit = defineEmits(['select-item', 'reset-group','refresh-group-list']);


const showModal = ref(false);


const studentColumns = computed(() => studentColumnsBase);

// componentKey supprimé - utilisation directe de setData() pour les mises à jour
const selectedStudent = ref(null);

// Champs filtres dynamiques utilisateur
const filterField = ref('');
const filterType = ref('=');
const filterValue = ref('');

const refreshSelectedStudent = async () => {
  if (!selectedStudent.value || !selectedStudent.value._id) return;
  try {
    const { data } = await api.get(`/students/${selectedStudent.value._id}`);
    selectedStudent.value = data;
  } catch (err) {
    console.error("❌ Erreur lors de la mise à jour de la fiche étudiante :", err);
  }
};



const updateStudent = async (updatedData) => {
  try {
    const allowedFields = [
      'firstname',
      'lastname',
      'email',
      'institution',
      'sector',
      'grade',
      'location',
      'zipcode'
    ];

    const filteredData = Object.fromEntries(
      Object.entries(updatedData).filter(([key]) => allowedFields.includes(key))
    );

    await api.put(`/students/${updatedData._id}`, filteredData);
    console.log("✅ Student updated:", filteredData);

    // Vider le cache et recharger les données fraîches
    if (tabulatorRef.value?.clearCache) {
      tabulatorRef.value.clearCache();
    }

    // 🔁 Refresh la table Tabulator (méthode corrigée)
    if (tabulatorRef.value?.getTable?.()) {
      await tabulatorRef.value.getTable().setData();
    } else {
      console.warn("🔍 Impossible de rafraîchir la table : instance Tabulator introuvable");
    }


  } catch (err) {
    console.error("❌ Failed to update student:", err.response?.data || err.message);
  }
};

const selectedStudents = ref([]);

// Modifier la fonction de gestion des sélections
function handleSelectedStudents(students) {
  // Maintenant nous recevons toujours un tableau
  selectedStudents.value = students;
  console.log(`✅ ${students.length} étudiants sélectionnés`);
}



// 🎯 Recalcul des filtres envoyés à TabulatorRemote
const computedFilters = computed(() => {
  const filters = [];
  if (props.filterGroupId) {
    filters.push({ field: 'group', type: '=', value: props.filterGroupId });
  }
  console.log("🎯 Filtres calculés:", filters, "pour groupId:", props.filterGroupId);
  /* if (filterField.value && filterValue.value) {
      filters.push({
          field: filterField.value,
          type: filterType.value,
          value: filterValue.value,
      });
  } */
  return filters;
});


watch(() => props.refreshGroupOptions, (newVal, oldVal) => {
  // Ne recharge que si la valeur a vraiment changé
  if (newVal !== oldVal && newVal > 0) {
    loadGroups();
  }
});

// 🔁 Rafraîchit la table si le groupe change (optimisé et corrigé)
watch(() => props.filterGroupId, (newVal, oldVal) => {
  console.log("🟢 Nouveau groupId reçu :", newVal, "ancien:", oldVal);
  
  // Vider le cache pour que les nouveaux filtres prennent effet
  if (tabulatorRef.value?.clearCache) {
    tabulatorRef.value.clearCache();
  }
  
  // Forcer le rechargement avec les nouveaux filtres
  if (tabulatorRef.value?.getTable?.()) {
    tabulatorRef.value.getTable().setData();
  } else {
    // Fallback si l'instance n'est pas encore prête
    setTimeout(() => {
      if (tabulatorRef.value?.getTable?.()) {
        tabulatorRef.value.getTable().setData();
      }
    }, 100);
  }
});

// 👉 Action lorsqu’une ligne est cliquée
function handleRowClick(student) {
  console.log("Élève cliqué :", student);
  selectedStudent.value = student;
  showModal.value = true;
}

// ✉️ Émettre vers parent
function emitItemSelected(student) {
  emit('select-item', student);
}

// Modifiez la fonction deleteStudent (optimisée avec vidage de cache)
async function deleteStudent(studentId) {
  try {
    const response = await api.delete(`/students/${studentId}`);

    // Vider le cache et recharger les données fraîches
    if (tabulatorRef.value?.clearCache) {
      tabulatorRef.value.clearCache();
    }
    
    // Méthode optimale avec accès direct à l'instance
    if (tabulatorRef.value?.getTable?.()) {
      await tabulatorRef.value.getTable().setData();
      console.log("Table refreshed after deletion");
    } else {
      // Fallback - force reload via setData si l'instance n'est pas disponible
      setTimeout(() => {
        if (tabulatorRef.value?.getTable?.()) {
          tabulatorRef.value.getTable().setData();
        }
      }, 100);
    }

    alert("Étudiant supprimé avec succès");
  } catch (err) {
    if (err.response?.status >= 500) {
      console.error("Server error:", err);
      alert("Erreur serveur lors de la suppression");
    }
    // Les 400/404 sont silencieux (traités comme des cas normaux)
  }
}


// 🔥 Suppression de tous les étudiants (optimisé avec vidage de cache)
async function deleteAllStudents() {
  if (!confirm('Are you sure you want to delete all students?')) return;
  try {
    await api.delete(`/students`);
    alert('All students deleted successfully');
    
    // Vider le cache et recharger les données fraîches
    if (tabulatorRef.value?.clearCache) {
      tabulatorRef.value.clearCache();
    }
    
    // Utilise setData() au lieu de forcer un rechargement complet
    if (tabulatorRef.value?.getTable?.()) {
      tabulatorRef.value.getTable().setData();
    }
  } catch (err) {
    console.error('Error deleting students:', err);
    alert('Failed to delete all students');
  }
}

// 🔄 Réinitialisation du filtre groupe (optimisé)
function resetGroupFilter() {
  emit('reset-group');
  // Utilise setData() au lieu de forcer un rechargement complet
  if (tabulatorRef.value?.getTable?.()) {
    tabulatorRef.value.getTable().setData();
  }
  alert('Group filter reset.');
}

// 🧼 Nettoyage des filtres utilisateur
function clearFilter() {
  filterField.value = '';
  filterType.value = '=';
  filterValue.value = '';
}

const selectedGroupId = ref("");
const groupList = ref([]);
const tabulatorRef = ref(null);

// Statistics data
const statistics = ref({
  totalStudents: 0,
  totalInstitutions: 0,
  totalGrades: 0
});

// Fetch statistics
const fetchStatistics = async () => {
  try {
    const response = await api.get('/students/statistics');
    statistics.value = response.data;
  } catch (error) {
    console.error('Error fetching student statistics:', error);
  }
};

// Charger la liste des groupes (optimisé)
const loadGroups = async () => {
  try {
    const res = await api.get(`/groups`);
    groupList.value = res.data.groups;
  } catch (err) {
    console.error("❌ Failed to load groups:", err);
  }
};

// Déplacer l'appel dans onMounted pour éviter les appels multiples
onMounted(() => {
  loadGroups();
  fetchStatistics();
});

const addSelectedToGroup = async () => {
  if (selectedStudents.value.length === 0) {
    showToast("Aucun étudiant sélectionné", "danger");
    emit("refresh-group-list");
    return;
  }

  const studentIds = Array.isArray(selectedStudents.value)
    ? selectedStudents.value.map(s => s._id)
    : [];
  console.log("✅ Étudiants à ajouter :", studentIds);


  await api.put(`/groups/${selectedGroupId.value}/add-students`, {
    studentIds
  });

  showToast(`${studentIds.length} étudiant(s) ajouté(s) au groupe !`, "success");

  await refreshSelectedStudent();

};

const successToast = ref(null);
const toastMessage = ref("");

let bsToast = null;

onMounted(() => {
  const Toast = window.bootstrap?.Toast;
  if (Toast && successToast.value) {
    bsToast = new Toast(successToast.value, { delay: 4000 });
  }

});

function showToast(message) {
  toastMessage.value = message;
  bsToast?.show();
}

function hideToast() {
  bsToast?.hide();
}

function openStudentModal(student) {
  selectedStudent.value = student;
  showModal.value = true;
}



</script>

<style scoped>
.toast-container {
  pointer-events: none;
}

.toast {
  pointer-events: auto;
}

/* Statistics Indicators */
.statistics-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin: 120px 20px 20px 20px;
  padding: 0;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  flex-shrink: 0;
}

.stat-content {
  flex-grow: 1;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Blue theme */
.stat-card-blue {
  border-left-color: #6CB4EE;
}

.stat-card-blue .stat-icon {
  background-color: #E3F2FD;
  color: #1976D2;
}

.stat-card-blue .stat-value {
  color: #1565C0;
}

/* Green theme */
.stat-card-green {
  border-left-color: #77DD77;
}

.stat-card-green .stat-icon {
  background-color: #E8F5E9;
  color: #388E3C;
}

.stat-card-green .stat-value {
  color: #2E7D32;
}

/* Purple theme */
.stat-card-purple {
  border-left-color: #B19CD9;
}

.stat-card-purple .stat-icon {
  background-color: #F3E5F5;
  color: #7B1FA2;
}

.stat-card-purple .stat-value {
  color: #6A1B9A;
}

/* Responsive design for statistics */
@media (max-width: 768px) {
  .statistics-container {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 15px;
  }

  .stat-card {
    padding: 15px;
  }

  .stat-icon {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }

  .stat-value {
    font-size: 1.5rem;
  }
}

.StudentListContainer {
  padding: 10px;
  border: 1px gainsboro solid;
  margin-top: 20px;
  background-color: aliceblue;
}

.mr-2 {
  margin-right: 20px;
}
</style>